/**
 * Low-level pointer-tap detection.
 *
 * Every selectable in the viewport (scene objects, the 3D cursor, lights,
 * empties) needs the same gesture: distinguish a tap from a drag, track
 * the originating pointer type so touch hold-to-add can register, and
 * expose the shift-key state at release time. That logic is small but
 * non-trivial, and copying it into each selection handler would mean we
 * have three places to tune the drag threshold or swap the timing source.
 *
 * `useTapDetection` owns only the gesture. Higher-level selection
 * semantics — additive modifiers, blend-entry monolithic selection,
 * double-tap callbacks, cycle dispatch — live in the caller so each
 * selectable remains free to compose its own policy on top.
 */

import { useCallback, useRef } from "react";
import {
  HOLD_TO_ADD_MS,
  TAP_DRAG_THRESHOLD_SQ,
} from "./interactionConstants.ts";

/**
 * Resolved information about a clean (non-drag) tap, returned from
 * `readTap`. Consumers read this and decide how to dispatch.
 */
export type TapInfo = {
  clientX: number;
  clientY: number;
  pointerType: string;
  /** True when this tap should be treated as additive selection (touch hold). */
  holdToAdd: boolean;
  /** Shift modifier at pointer-up time. */
  shiftKey: boolean;
  /** Alt modifier at pointer-up time — used to drill up to a parent collection. */
  altKey: boolean;
  /** R3F's full depth-ordered intersection list at the click point. */
  intersections: readonly any[];
};

/**
 * Returns:
 *  - `handlePointerDown`: attach to the mesh's `onPointerDown` to arm
 *    the detector (records position, time, pointer type).
 *  - `readTap`: call from `onPointerUp`. Returns a `TapInfo` if this
 *    release counts as a tap (drag-free primary button), or `null`
 *    otherwise. The caller is responsible for calling
 *    `e.stopPropagation()` when it decides to consume the tap.
 */
export function useTapDetection() {
  const downState = useRef<{
    x: number;
    y: number;
    time: number;
    pointerType: string;
  } | null>(null);

  const handlePointerDown = useCallback((e: any) => {
    const ne = e.nativeEvent as PointerEvent;
    if (ne.button !== 0) return;
    downState.current = {
      x: ne.clientX,
      y: ne.clientY,
      time: performance.now(),
      pointerType: ne.pointerType,
    };
  }, []);

  const readTap = useCallback((e: any): TapInfo | null => {
    const ne = e.nativeEvent as PointerEvent;
    if (ne.button !== 0) return null;
    const down = downState.current;
    downState.current = null;
    if (!down) return null;

    const dx = ne.clientX - down.x;
    const dy = ne.clientY - down.y;
    if (dx * dx + dy * dy > TAP_DRAG_THRESHOLD_SQ) return null;

    // Touch hold-to-add: a sustained press without drag is treated as
    // additive so touch users can build selections without modifier keys.
    const holdToAdd =
      down.pointerType === "touch" &&
      performance.now() - down.time >= HOLD_TO_ADD_MS;

    return {
      clientX: ne.clientX,
      clientY: ne.clientY,
      pointerType: down.pointerType,
      holdToAdd,
      shiftKey: ne.shiftKey,
      altKey: ne.altKey,
      intersections: e.intersections ?? [],
    };
  }, []);

  return { handlePointerDown, readTap };
}
